import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Square, RotateCcw, ShieldCheck, AlertCircle, Loader2, Check, Camera as CameraIcon } from "lucide-react";
import { recordMotorSession } from "@/lib/neuroMotorStore";

// -----------------------------------------------------------------------
// CameraRecorder — on-device webcam motion capture.
//
// PRIVACY-FIRST:
//   • Requests explicit camera permission via getUserMedia.
//   • Never uploads the video. Frames are analysed in the browser only.
//   • On stop, the MediaStream is released and the blob is discarded unless
//     the user explicitly opts to save (we don't save by default).
//
// NON-DIAGNOSTIC. Frame-difference motion is an experimental computer-vision
// estimate, not a clinical range-of-motion measurement.
// -----------------------------------------------------------------------

const MAX_MS = 15000;
const SAMPLE_HZ = 6; // frame-diff samples per second

export default function CameraRecorder({
  kind = "movement",
  level = 1,
  prompt = "Perform the guided movement in front of the camera.",
  onDone,
  testId = "motor-camera",
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const prevFrameRef = useRef(null);
  const sampleTimerRef = useRef(null);
  const stopTimerRef = useRef(null);
  const motionRef = useRef([]);

  const [state, setState] = useState("idle"); // idle | requesting | ready | recording | analysing | done | error
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [result, setResult] = useState(null);
  const [supported] = useState(
    typeof window !== "undefined" &&
      !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)
  );

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    try {
      clearInterval(sampleTimerRef.current);
      clearTimeout(stopTimerRef.current);
      if (recRef.current && recRef.current.state !== "inactive") {
        try { recRef.current.stop(); } catch { /* ignore */ }
      }
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
      streamRef.current = null;
      chunksRef.current = [];
      motionRef.current = [];
      prevFrameRef.current = null;
    } catch { /* ignore */ }
  };

  const requestCamera = async () => {
    if (!supported) {
      setError("Camera recording isn't supported on this device or browser.");
      setState("error");
      return;
    }
    setError(null);
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        v.playsInline = true;
        v.muted = true;
        await v.play().catch(() => { /* autoplay guard */ });
      }
      setState("ready");
    } catch (e) {
      const denied = /permission|not allowed|denied/i.test(String(e?.message || e));
      setError(denied ? "Camera access was denied. Enable camera permission and try again." : "Unable to access the camera.");
      setState("error");
    }
  };

  const start = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    motionRef.current = [];
    prevFrameRef.current = null;
    setElapsedMs(0);

    try {
      const mime =
        window.MediaRecorder.isTypeSupported?.("video/webm;codecs=vp9")
          ? "video/webm;codecs=vp9"
          : window.MediaRecorder.isTypeSupported?.("video/webm")
          ? "video/webm"
          : "";
      const rec = mime
        ? new MediaRecorder(streamRef.current, { mimeType: mime })
        : new MediaRecorder(streamRef.current);
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => finalise();
      recRef.current = rec;
      rec.start(400);
    } catch (e) {
      setError("Recording could not start on this device.");
      setState("error");
      return;
    }

    const t0 = performance.now();
    setState("recording");
    sampleTimerRef.current = setInterval(() => {
      const e = performance.now() - t0;
      setElapsedMs(e);
      sampleFrame();
      if (e >= MAX_MS) stop();
    }, Math.round(1000 / SAMPLE_HZ));

    stopTimerRef.current = setTimeout(() => stop(), MAX_MS + 200);
  };

  // Sample a downscaled frame; compute mean absolute pixel diff vs prev.
  const sampleFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || v.videoWidth === 0) return;
    const w = 96, h = 72;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, w, h);
    const frame = ctx.getImageData(0, 0, w, h).data;
    const prev = prevFrameRef.current;
    if (prev) {
      let sum = 0;
      // Sample every 4th pixel for speed; use luminance approx.
      for (let i = 0; i < frame.length; i += 16) {
        const lum = 0.299 * frame[i] + 0.587 * frame[i + 1] + 0.114 * frame[i + 2];
        const plum = 0.299 * prev[i] + 0.587 * prev[i + 1] + 0.114 * prev[i + 2];
        sum += Math.abs(lum - plum);
      }
      const samples = frame.length / 16;
      motionRef.current.push(sum / samples); // 0..255
    }
    prevFrameRef.current = frame;
  };

  const stop = () => {
    clearInterval(sampleTimerRef.current);
    clearTimeout(stopTimerRef.current);
    sampleTimerRef.current = null;
    stopTimerRef.current = null;
    try { recRef.current?.stop(); } catch { /* ignore */ }
  };

  const finalise = () => {
    setState("analysing");
    // Compute movement metrics from motion samples.
    const arr = motionRef.current;
    // Discard video buffer immediately (privacy).
    chunksRef.current = [];
    if (!arr.length) {
      setError("No frames captured — please try again.");
      setState("error");
      return;
    }
    const avg = arr.reduce((a, x) => a + x, 0) / arr.length;
    const peak = Math.max(...arr);
    const mean = avg;
    const variance = arr.reduce((a, x) => a + (x - mean) ** 2, 0) / arr.length;
    const stdev = Math.sqrt(variance);
    // "Movement intensity" — bound to 0..100.
    const intensity = Math.min(100, Math.round((avg / 40) * 100));
    // "Consistency" — 100 when stdev is small relative to mean.
    const consistency = Math.max(0, Math.min(100, Math.round(100 - (stdev / (mean + 4)) * 60)));
    // Rough "repetitions" — count zero-crossings above 60% of mean.
    let reps = 0, above = false;
    arr.forEach((v) => {
      const on = v > mean;
      if (on && !above) reps += 1;
      above = on;
    });
    const durationMs = Math.round(arr.length * (1000 / SAMPLE_HZ));
    // Detection: was there detectable guided movement?
    const detected = intensity >= 15;

    const metrics = {
      intensity, consistency, reps, peak: Math.round(peak),
      duration_ms: durationMs, detected,
    };
    setResult(metrics);

    // Record device-only session (no video, only aggregate numbers).
    recordMotorSession({
      kind,
      level,
      accuracy: detected ? intensity : 0,
      completion_ms: durationMs,
      errors: detected ? 0 : 1,
      correct: reps,
      total: Math.max(reps, 1),
      assisted: false,
    });

    setState("done");
    onDone?.(metrics);
  };

  const restart = () => {
    setResult(null);
    setError(null);
    setElapsedMs(0);
    setState(streamRef.current ? "ready" : "idle");
  };

  const stopCamera = () => { cleanup(); setState("idle"); };

  const percent = Math.min(100, Math.round((elapsedMs / MAX_MS) * 100));
  const secs = (elapsedMs / 1000).toFixed(1);

  return (
    <div data-testid={testId} className="rounded-3xl border border-orange-400/25 bg-orange-400/[0.05] p-5 sm:p-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
          Movement Lab · experimental · device-only
        </div>
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          <ShieldCheck className="w-3 h-3" /> No video leaves this device
        </div>
      </div>

      {prompt && (
        <div className="mt-3 rounded-2xl border border-white/10 bg-black/40 p-4">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">Guided movement</div>
          <div className="mt-1 font-display text-lg sm:text-xl font-semibold text-white leading-snug">{prompt}</div>
        </div>
      )}

      {/* Camera surface */}
      <div className="mt-4 relative w-full aspect-[4/3] max-w-2xl mx-auto rounded-3xl border border-white/10 bg-black overflow-hidden">
        <video
          ref={videoRef}
          data-testid={`${testId}-video`}
          className="w-full h-full object-cover"
          style={{ transform: "scaleX(-1)" }}
          playsInline
          muted
        />
        <canvas ref={canvasRef} className="hidden" />
        {(state === "idle" || state === "requesting") && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-center px-6">
            <div>
              <CameraIcon className="w-10 h-10 text-orange-300 mx-auto" />
              <div className="mt-3 font-semibold text-white text-lg">Camera-based movement practice</div>
              <p className="mt-2 text-xs text-zinc-400 max-w-sm">
                Requires camera permission. Nothing is recorded to the cloud — all
                motion analysis happens on this device. You can deny access at any time.
              </p>
            </div>
          </div>
        )}
        {state === "recording" && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-red-500 text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-white" /> Recording · {secs}s
          </div>
        )}
      </div>

      {/* Progress bar during recording */}
      {state === "recording" && (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-red-500 transition-[width] duration-100" style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            Auto-stops after {Math.round(MAX_MS / 1000)}s
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {state === "idle" && (
          <button
            data-testid={`${testId}-request`}
            onClick={requestCamera}
            disabled={!supported}
            className="inline-flex items-center gap-2 rounded-full bg-orange-400 text-black font-semibold px-5 py-2.5 text-sm hover:bg-orange-300 disabled:opacity-50"
          >
            <CameraIcon className="w-4 h-4" /> Allow camera & continue
          </button>
        )}
        {state === "requesting" && (
          <div className="inline-flex items-center gap-2 text-sm text-zinc-300">
            <Loader2 className="w-4 h-4 animate-spin" /> Waiting for camera permission…
          </div>
        )}
        {state === "ready" && (
          <>
            <button
              data-testid={`${testId}-start`}
              onClick={start}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-400 to-red-400 text-black font-semibold px-6 py-3 text-base hover:from-orange-300 hover:to-red-300 shadow-[0_0_30px_-8px_rgba(251,146,60,0.7)]"
            >
              <Video className="w-5 h-5" /> Start recording
            </button>
            <button onClick={stopCamera} className="text-xs text-zinc-400 hover:text-white px-3 py-2">Turn off camera</button>
          </>
        )}
        {state === "recording" && (
          <button
            data-testid={`${testId}-stop`}
            onClick={stop}
            className="inline-flex items-center gap-2 rounded-full bg-red-500 text-white font-semibold px-6 py-3 text-base hover:bg-red-400"
          >
            <Square className="w-5 h-5" /> Stop
          </button>
        )}
        {state === "analysing" && (
          <div className="inline-flex items-center gap-2 text-sm text-orange-100">
            <Loader2 className="w-4 h-4 animate-spin" /> Analysing on device…
          </div>
        )}
        {(state === "done" || state === "error") && (
          <button
            data-testid={`${testId}-retry`}
            onClick={restart}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-white"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Try again with less assistance
          </button>
        )}
      </div>

      {/* Result */}
      <AnimatePresence>
        {state === "done" && result && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4"
          >
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold flex items-center gap-1.5">
              {result.detected ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              Motion analysis · experimental
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Intensity" val={`${result.intensity}%`} color="#fb923c" />
              <Stat label="Consistency" val={`${result.consistency}%`} color="#34d399" />
              <Stat label="Repetitions" val={result.reps} color="#22d3ee" />
              <Stat label="Duration" val={`${(result.duration_ms / 1000).toFixed(1)}s`} color="#a78bfa" />
            </div>
            <div className="mt-3 text-xs text-zinc-400">
              {result.detected
                ? "Guided movement detected. Task-performance only — not a clinical range-of-motion measurement."
                : "Little movement detected. Ensure you're centered in view and try again. Not a diagnosis."}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {state === "error" && error && (
        <div className="mt-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-100">
          {error}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, val, color }) => (
  <div className="rounded-xl border border-white/10 bg-black/30 p-3">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
    <div className="mt-0.5 font-display text-xl font-semibold tabular-nums" style={{ color }}>{val}</div>
  </div>
);
